import {
  abortConversation,
  getConversation,
  pushMessage,
  respondToBlock,
  sendMessage,
  type AgentSSEEvent,
} from '@api/agent';
import { devConsole } from '@helper-functions/devConsole';
import { agentPageContextAtom } from '@hooks/agentPanelAtoms';
import { useAtomValue } from 'jotai';
import posthog from 'posthog-js';
import { useCallback, useRef, useState } from 'react';
import type { BackendMessage, ChatMessage, ContentBlock, QueuedMessage } from './agentChatTypes';

const waitForRecovery = (ms = 1500) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Extract a human-readable string from a rich block answer payload so we can
 * re-send it as a plain message when clicking an expired block.
 */
function extractSelectionText(answer: Record<string, unknown>): string {
  if (typeof answer.selection === 'string') return answer.selection;
  if (typeof answer.label === 'string') return answer.label;
  if (answer.answers && typeof answer.answers === 'object') {
    return Object.values(answer.answers as Record<string, string>).join(', ');
  }
  if (Array.isArray(answer.selections)) {
    return (answer.selections as string[]).join(', ');
  }
  return JSON.stringify(answer);
}

/**
 * Options for the useAgentStream hook.
 */
export interface UseAgentStreamOptions {
  /** Optional callback when session is recovered from fallback (e.g., show toast) */
  onSessionRecovered?: () => void;
}

/**
 * Hook that manages SSE streaming, message accumulation, and history
 * loading for the agent chat panel. Extracted from the test UI page
 * so the same logic can be reused in the panel without duplication.
 */
export const useAgentStream = (
  token: string | undefined,
  conversationId: string,
  options?: UseAgentStreamOptions,
) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [messageQueue, setMessageQueue] = useState<QueuedMessage[]>([]);
  const [isRecovering, setIsRecovering] = useState(false);

  // Mirror caller-provided callbacks via a ref so callbacks downstream don't need
  // `options` (typically an inline object) in their dep arrays — that would rebuild
  // them every render.
  const onSessionRecoveredRef = useRef(options?.onSessionRecovered);
  onSessionRecoveredRef.current = options?.onSessionRecovered;

  // Read current page context so every message includes where the user is
  const pageContext = useAtomValue(agentPageContextAtom);

  // Refs avoid stale closures in the SSE callback — the callback fires
  // many times per render cycle, so useState would capture initial values.
  const blocksRef = useRef<ContentBlock[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);
  // Queue ref mirrors state for use inside SSE callback (same pattern as blocksRef)
  const messageQueueRef = useRef<QueuedMessage[]>([]);
  // Streaming ref so the send callback can read current streaming state without stale closure
  const streamingRef = useRef(false);
  // Ref to drainQueue function — breaks circular dependency with handleSseEvent
  const drainQueueRef = useRef<() => void>(() => {});

  /**
   * Replace the last streaming agent message with updated content.
   * Searches backwards from the end to find the streaming message, because
   * queued user messages may have been appended after the streaming agent message.
   * Uses functional updater so it always sees the latest state.
   */
  const updateStreamingMessage = useCallback((partial: Partial<ChatMessage>) => {
    setMessages((prev) => {
      const copy = [...prev];
      // Search backwards for the streaming agent message — queued user messages
      // may sit after it, so we can't assume it's the very last element.
      for (let i = copy.length - 1; i >= 0; i--) {
        if (copy[i].streaming) {
          copy[i] = { ...copy[i], ...partial };
          break;
        }
      }
      return copy;
    });
  }, []);

  /**
   * Mark all currently-running tool_call blocks as "done".
   * Called when any new event arrives, since that implies prior tools finished.
   */
  const finalizeRunningTools = useCallback(() => {
    blocksRef.current = blocksRef.current.map((block) =>
      block.type === 'tool_call' && block.status === 'running'
        ? { ...block, status: 'done' as const }
        : block,
    );
  }, []);

  /**
   * Mark all pending interactive rich blocks as "expired".
   * Called when the stream ends (done/error) so that rich blocks whose
   * backend waitForUserResponse timed out are no longer shown as pending.
   * Expired blocks remain clickable — clicking re-sends the choice as a
   * new message (handled in respondToRichBlock).
   */
  const expirePendingRichBlocks = useCallback(() => {
    blocksRef.current = blocksRef.current.map((block) =>
      block.type === 'rich_block' && block.status === 'pending'
        ? { ...block, status: 'expired' as const }
        : block,
    );
  }, []);

  /**
   * Process a single SSE event from the sendMessage stream.
   * Builds an ordered list of content blocks so tool calls appear
   * interspersed with text in their actual execution order.
   */
  const handleSseEvent = useCallback(
    (evt: AgentSSEEvent) => {
      switch (evt.event) {
        case 'thinking': {
          finalizeRunningTools();
          const last = blocksRef.current[blocksRef.current.length - 1];
          if (last?.type === 'thinking') {
            last.content += (evt.data.thinking as string) ?? '';
          } else {
            blocksRef.current.push({
              type: 'thinking',
              content: (evt.data.thinking as string) ?? '',
            });
          }
          updateStreamingMessage({ blocks: [...blocksRef.current] });
          break;
        }
        case 'text': {
          finalizeRunningTools();
          const last = blocksRef.current[blocksRef.current.length - 1];
          if (last?.type === 'text') {
            last.content += (evt.data.text as string) ?? '';
          } else {
            blocksRef.current.push({
              type: 'text',
              content: (evt.data.text as string) ?? '',
            });
          }
          updateStreamingMessage({ blocks: [...blocksRef.current] });
          break;
        }
        case 'tool_use': {
          finalizeRunningTools();
          blocksRef.current.push({
            type: 'tool_call',
            toolUseId: (evt.data.toolUseId as string) ?? '',
            name: (evt.data.name as string) ?? 'tool',
            input: (evt.data.input as Record<string, unknown>) ?? {},
            status: 'running',
          });
          updateStreamingMessage({ blocks: [...blocksRef.current] });
          break;
        }
        case 'tool_result': {
          const targetId = evt.data.toolUseId as string;
          const toolBlock = blocksRef.current.find(
            (b): b is Extract<ContentBlock, { type: 'tool_call' }> =>
              b.type === 'tool_call' && b.toolUseId === targetId,
          );
          if (toolBlock) {
            toolBlock.result = evt.data.content;
            toolBlock.status = 'done';
          }
          updateStreamingMessage({ blocks: [...blocksRef.current] });
          break;
        }
        case 'rich_block': {
          finalizeRunningTools();
          const isInteractive = evt.data.interactive as boolean;
          blocksRef.current.push({
            type: 'rich_block',
            blockId: (evt.data.blockId as string) ?? '',
            blockType: (evt.data.type as string) ?? '',
            interactive: isInteractive,
            component: (evt.data.component as string) ?? '',
            payload: (evt.data.payload as Record<string, unknown>) ?? {},
            // Non-interactive blocks are immediately "answered" (display-only)
            status: isInteractive ? 'pending' : 'answered',
          });
          updateStreamingMessage({ blocks: [...blocksRef.current] });
          break;
        }
        case 'done':
          finalizeRunningTools();
          expirePendingRichBlocks();
          updateStreamingMessage({ blocks: [...blocksRef.current], streaming: false });
          blocksRef.current = [];
          setStreaming(false);
          streamingRef.current = false;
          // Drain queued messages now that the stream finished
          drainQueueRef.current();
          break;
        case 'error':
          finalizeRunningTools();
          expirePendingRichBlocks();
          blocksRef.current.push({
            type: 'text',
            content: `Error: ${(evt.data.error as string) ?? (evt.data.message as string) ?? 'Unknown error from agent'}`,
          });
          updateStreamingMessage({ blocks: [...blocksRef.current], streaming: false });
          blocksRef.current = [];
          setStreaming(false);
          streamingRef.current = false;
          // Drain queued messages even after error — preserve user intent
          drainQueueRef.current();
          break;
        case 'fallback':
          // Session was lost (pod restart) but backend recovered from DB.
          // This is transparent to the user — just notify via callback if provided.
          devConsole.info('[useAgentStream] Session recovered via fallback:', evt.data);
          onSessionRecoveredRef.current?.();
          break;
        default:
          break;
      }
    },
    [finalizeRunningTools, expirePendingRichBlocks, updateStreamingMessage],
  );

  /**
   * Drain the message queue after the current stream finishes.
   * Single queued message sends as-is; multiple messages are batched into
   * a numbered list (matching the theia reference pattern) so the agent
   * can process them all at once.
   */
  const drainQueue = useCallback(() => {
    const queued = messageQueueRef.current;
    if (queued.length === 0) return;

    // Clear the queue immediately
    messageQueueRef.current = [];
    setMessageQueue([]);

    // Build the prompt — single message sends as-is, multiple get batched
    let batchedPrompt: string;
    let batchedImages: string[] | undefined;

    if (queued.length === 1) {
      batchedPrompt = queued[0].prompt;
      batchedImages = queued[0].images;
    } else {
      // Batch multiple messages with context prefix so the agent understands
      const numbered = queued.map((q, i) => `${i + 1}. ${q.prompt}`).join('\n');
      batchedPrompt = [
        'While you were working, I sent these follow-up messages:',
        numbered,
        'Please review all of them (later messages may override earlier ones) and respond.',
      ].join('\n');
      // Combine all images from all queued messages
      batchedImages = queued.flatMap((q) => q.images ?? []);
      if (batchedImages.length === 0) batchedImages = undefined;
    }

    // User messages are already in the chat history (added at queue time).
    // Clear the queued flag on those messages now that they're being sent,
    // then add the agent placeholder and start streaming.
    setMessages((prev) => [
      ...prev.map((msg) => (msg.queued ? { ...msg, queued: false } : msg)),
      { role: 'agent', blocks: [], streaming: true, timestamp: new Date().toISOString() },
    ]);
    setStreaming(true);
    streamingRef.current = true;
    blocksRef.current = [];

    const controller = new AbortController();
    abortControllerRef.current = controller;

    sendMessage(
      token,
      conversationId,
      batchedPrompt,
      pageContext,
      handleSseEvent,
      controller.signal,
      batchedImages,
    )
      .catch(async (err) => {
        // AbortError is expected when user clicks Stop
        if (err instanceof DOMException && err.name === 'AbortError') return;

        // Connection lost during drain — auto-retry with same batched prompt.
        setStreaming(false);
        streamingRef.current = false;
        setIsRecovering(true);

        try {
          await waitForRecovery();

          // Auto-retry the batched messages
          setMessages((prev) => {
            const withoutStreaming = prev.filter((m) => !m.streaming);
            return [
              ...withoutStreaming,
              { role: 'agent', blocks: [], streaming: true, timestamp: new Date().toISOString() },
            ];
          });
          setStreaming(true);
          streamingRef.current = true;
          blocksRef.current = [];

          const retryController = new AbortController();
          abortControllerRef.current = retryController;

          await sendMessage(
            token,
            conversationId,
            batchedPrompt,
            pageContext,
            handleSseEvent,
            retryController.signal,
            batchedImages,
          );
        } catch (retryErr) {
          posthog.capture('agent-chat:user_stream_error', {
            organizationId: pageContext.organizationId,
            workspaceId: pageContext.workspaceId,
            conversationId,
            error: retryErr instanceof Error ? retryErr.message : String(retryErr),
          });
          setStreaming(false);
          streamingRef.current = false;
          setMessages((prev) => {
            const withoutStreaming = prev.filter((m) => !m.streaming);
            return [
              ...withoutStreaming,
              {
                role: 'agent' as const,
                blocks: [
                  {
                    type: 'text' as const,
                    content: `Failed to reach agent: ${retryErr instanceof Error ? retryErr.message : String(retryErr)}. Please try again.`,
                  },
                ],
                streaming: false,
                timestamp: new Date().toISOString(),
              },
            ];
          });
        } finally {
          setIsRecovering(false);
        }
      })
      .finally(() => {
        abortControllerRef.current = null;
      });
  }, [token, conversationId, handleSseEvent, pageContext]);

  // Keep the ref in sync so handleSseEvent can call it without circular deps
  drainQueueRef.current = drainQueue;

  /**
   * Send a user message and start streaming the agent's response.
   * If the agent is currently streaming, the message is queued instead
   * and will be sent automatically when the current stream finishes.
   * Optionally includes pasted images as base64 data URIs.
   */
  const send = useCallback(
    async (prompt: string, images?: string[]) => {
      const trimmed = prompt.trim();
      if (!trimmed || !token || !conversationId) return;

      // If currently streaming, try pushing into the active stream first (PLAT-3271).
      // If push fails (no active stream, network error), fall back to batch queue.
      if (streamingRef.current) {
        // Show message immediately BELOW the streaming response (queued indicator)
        setMessages((prev) => [
          ...prev,
          {
            role: 'user',
            text: trimmed,
            images: images?.length ? images : undefined,
            queued: true,
            timestamp: new Date().toISOString(),
          },
        ]);

        // Path A: try pushing into the active agent stream
        try {
          const result = await pushMessage(token, conversationId, trimmed);
          if (result.pushed) {
            // Accepted — move the message ABOVE the streaming agent response.
            // The visual reorder signals to the user that the agent will
            // address this message as part of the current response.
            setMessages((prev) => {
              // Find the streaming agent message
              const streamIdx = prev.findIndex((m) => m.streaming);
              if (streamIdx === -1) return prev;

              // Find the first queued user message after the streaming message
              // that matches our text (handles concurrent pushes correctly)
              let targetIdx = -1;
              for (let i = streamIdx + 1; i < prev.length; i++) {
                if (prev[i].queued && prev[i].text === trimmed) {
                  targetIdx = i;
                  break;
                }
              }
              if (targetIdx === -1) return prev;

              // Remove from current position and clear queued flag
              const msg = { ...prev[targetIdx], queued: false };
              const without = [...prev.slice(0, targetIdx), ...prev.slice(targetIdx + 1)];

              // Re-find streaming index (shifted by removal)
              const newStreamIdx = without.findIndex((m) => m.streaming);
              if (newStreamIdx === -1) return without;

              // Insert just before the streaming message
              return [...without.slice(0, newStreamIdx), msg, ...without.slice(newStreamIdx)];
            });
            return;
          }
        } catch {
          // Push failed (409, network error, etc.) — fall through to Path B
        }

        // Path B fallback: add to batch queue (will drain after stream ends)
        const queued: QueuedMessage = {
          prompt: trimmed,
          images: images?.length ? images : undefined,
        };
        messageQueueRef.current = [...messageQueueRef.current, queued];
        setMessageQueue([...messageQueueRef.current]);
        return;
      }

      // Add user message (with optional images) + placeholder streaming agent message
      const now = new Date().toISOString();
      setMessages((prev) => [
        ...prev,
        {
          role: 'user',
          text: trimmed,
          images: images?.length ? images : undefined,
          timestamp: now,
        },
        { role: 'agent', blocks: [], streaming: true, timestamp: now },
      ]);
      setStreaming(true);
      streamingRef.current = true;
      blocksRef.current = [];

      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        await sendMessage(
          token,
          conversationId,
          trimmed,
          pageContext,
          handleSseEvent,
          controller.signal,
          images,
        );
      } catch (err) {
        // AbortError is expected when user clicks Stop
        if (err instanceof DOMException && err.name === 'AbortError') return;

        // Connection lost (tab backgrounded, network interruption, proxy timeout).
        // Instead of asking the user to continue manually, we auto-retry once.
        // The backend's fallback mechanism will reconstruct context from PostgreSQL
        // if the session was lost due to a pod restart.
        setStreaming(false);
        streamingRef.current = false;
        setIsRecovering(true);

        try {
          // Wait briefly for backend to stabilize after potential pod restart
          await waitForRecovery();

          // Auto-retry: start a fresh stream with the same prompt.
          // The backend will either resume the session or use the fallback mechanism.
          // We DON'T replace messages — keep existing UI state and just restart the stream.
          setMessages((prev) => {
            // Remove any incomplete streaming messages
            const withoutStreaming = prev.filter((m) => !m.streaming);
            return [
              ...withoutStreaming,
              { role: 'agent', blocks: [], streaming: true, timestamp: new Date().toISOString() },
            ];
          });
          setStreaming(true);
          streamingRef.current = true;
          blocksRef.current = [];

          const retryController = new AbortController();
          abortControllerRef.current = retryController;

          await sendMessage(
            token,
            conversationId,
            trimmed,
            pageContext,
            handleSseEvent,
            retryController.signal,
            images,
          );
        } catch (retryErr) {
          // Retry also failed — now show an error, but keep existing messages visible
          posthog.capture('agent-chat:user_stream_error', {
            organizationId: pageContext.organizationId,
            workspaceId: pageContext.workspaceId,
            conversationId,
            error: retryErr instanceof Error ? retryErr.message : String(retryErr),
          });
          setStreaming(false);
          streamingRef.current = false;
          setMessages((prev) => {
            const withoutStreaming = prev.filter((m) => !m.streaming);
            return [
              ...withoutStreaming,
              {
                role: 'agent' as const,
                blocks: [
                  {
                    type: 'text' as const,
                    content: `Failed to reach agent: ${retryErr instanceof Error ? retryErr.message : String(retryErr)}. Please try again.`,
                  },
                ],
                streaming: false,
                timestamp: new Date().toISOString(),
              },
            ];
          });
        } finally {
          setIsRecovering(false);
        }
      } finally {
        abortControllerRef.current = null;
      }
    },
    [token, conversationId, handleSseEvent, pageContext],
  );

  /**
   * Stop the currently-streaming agent response.
   * Aborts both client-side fetch and server-side Claude SDK stream.
   */
  const stopStreaming = useCallback(async () => {
    if (!streaming || !conversationId) return;

    // 1. Abort client-side fetch
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;

    // 2. Signal server to stop
    if (token) {
      try {
        await abortConversation(token, conversationId);
      } catch {
        // Stream may have already ended — safe to ignore
      }
    }

    // 3. Clear any queued messages — user explicitly stopped, don't auto-send
    messageQueueRef.current = [];
    setMessageQueue([]);

    // 4. Finalize UI
    finalizeRunningTools();
    blocksRef.current.push({ type: 'text', content: '\n\n_(Response stopped by user)_' });
    updateStreamingMessage({ blocks: [...blocksRef.current], streaming: false });
    blocksRef.current = [];
    setStreaming(false);
    streamingRef.current = false;
  }, [streaming, conversationId, token, finalizeRunningTools, updateStreamingMessage]);

  /**
   * Submit the user's response to an interactive rich block.
   *
   * For pending blocks (agent is still waiting): updates the UI and POSTs to
   * the backend to unblock the agent stream.
   *
   * For expired blocks (timeout elapsed, stream already finished): marks the
   * block as answered in the UI and re-sends the user's selection as a new
   * message so the agent can process it in a fresh turn.
   */
  const respondToRichBlock = useCallback(
    async (blockId: string, answer: Record<string, unknown>) => {
      // Check if this block is expired — either in the current streaming
      // blocks or in a historical (non-streaming) message.
      const liveBlock = blocksRef.current.find(
        (b): b is Extract<ContentBlock, { type: 'rich_block' }> =>
          b.type === 'rich_block' && b.blockId === blockId,
      );
      const isExpired = liveBlock?.status === 'expired' || (!liveBlock && !streamingRef.current);

      // Track rich block interaction
      posthog.capture('agent-chat:user_rich-block_interacted', {
        organizationId: pageContext.organizationId,
        workspaceId: pageContext.workspaceId,
        conversationId,
        blockType: liveBlock?.blockType ?? 'unknown',
        isExpired,
      });

      if (isExpired) {
        // Mark the block as answered in the UI for visual feedback
        setMessages((prev) =>
          prev.map((msg) => {
            if (!msg.blocks) return msg;
            const updated = msg.blocks.map((b) => {
              if (b.type === 'rich_block' && b.blockId === blockId) {
                return { ...b, response: answer, status: 'answered' as const };
              }
              return b;
            });
            return { ...msg, blocks: updated };
          }),
        );

        // Re-send the user's choice as a new message so the agent processes it
        const selection = extractSelectionText(answer);
        await send(`I selected: ${selection}`);
        return;
      }

      // --- Pending block: normal path ---
      if (liveBlock) {
        liveBlock.response = answer;
        liveBlock.status = 'answered';
        updateStreamingMessage({ blocks: [...blocksRef.current] });
      }

      // Also update blocks in non-streaming messages (for history)
      setMessages((prev) =>
        prev.map((msg) => {
          if (!msg.blocks) return msg;
          const updated = msg.blocks.map((b) => {
            if (b.type === 'rich_block' && b.blockId === blockId) {
              return { ...b, response: answer, status: 'answered' as const };
            }
            return b;
          });
          return { ...msg, blocks: updated };
        }),
      );

      // POST to backend to unblock the agent stream
      if (token && conversationId) {
        try {
          await respondToBlock(token, conversationId, blockId, answer);
        } catch (err) {
          console.error('[useAgentStream] Failed to submit rich block response:', err);
        }
      }
    },
    [
      token,
      conversationId,
      updateStreamingMessage,
      send,
      pageContext.organizationId,
      pageContext.workspaceId,
    ],
  );

  /**
   * Load message history for a conversation from the backend.
   * Transforms backend message format into the ChatMessage[] the UI expects.
   *
   * Uses the `events` array when available for correct chronological ordering,
   * falling back to legacy separate arrays for older messages.
   */
  const loadHistory = useCallback(
    async (convoId: string) => {
      if (!token) return;

      try {
        const convo = await getConversation(token, convoId);
        // getConversation returns { ...conversation, messages: [...] }
        const backendMessages = (convo as unknown as { messages: BackendMessage[] }).messages;

        if (!backendMessages || backendMessages.length === 0) {
          setMessages([]);
          return;
        }

        const restored: ChatMessage[] = backendMessages.map((msg) => {
          if (msg.role === 'user') {
            return { role: 'user' as const, text: msg.content, timestamp: msg.createdAt };
          }

          const meta = msg.metadata || {};
          const blocks: ContentBlock[] = [];

          // NEW FORMAT: Use events array for correct chronological ordering
          if (Array.isArray(meta.events) && meta.events.length > 0) {
            // Sort events by timestamp then sequence to ensure correct order
            const sortedEvents = [...meta.events].sort((a, b) => {
              if (a.timestamp !== b.timestamp) {
                return a.timestamp - b.timestamp;
              }
              return a.sequence - b.sequence;
            });

            // Build a map of tool results for quick lookup
            const toolResultMap = new Map<string, unknown>();
            sortedEvents
              .filter((evt) => evt.type === 'tool_result')
              .forEach((evt) => {
                if (evt.type === 'tool_result') {
                  toolResultMap.set(evt.toolUseId, evt.content);
                }
              });

            // Convert events to content blocks, merging consecutive text/thinking
            sortedEvents.forEach((evt) => {
              const last = blocks[blocks.length - 1];

              switch (evt.type) {
                case 'thinking':
                  // Merge consecutive thinking blocks
                  if (last?.type === 'thinking') {
                    last.content += evt.thinking;
                  } else {
                    blocks.push({ type: 'thinking', content: evt.thinking });
                  }
                  break;

                case 'text':
                  // Merge consecutive text blocks
                  if (last?.type === 'text') {
                    last.content += evt.text;
                  } else {
                    blocks.push({ type: 'text', content: evt.text });
                  }
                  break;

                case 'tool_use':
                  blocks.push({
                    type: 'tool_call',
                    toolUseId: evt.toolUseId,
                    name: evt.name,
                    input: evt.input,
                    result: toolResultMap.get(evt.toolUseId),
                    status: 'done',
                  });
                  break;

                case 'tool_result':
                  // Tool results are merged into their tool_use blocks above
                  break;

                case 'rich_block':
                  blocks.push({
                    type: 'rich_block',
                    blockId: evt.blockId,
                    blockType: 'rich_block',
                    interactive: evt.interactive,
                    component: evt.component,
                    payload: evt.payload,
                    response: evt.response,
                    status: evt.response ? 'answered' : 'expired',
                  });
                  break;

                default:
                  break;
              }
            });

            return { role: 'agent' as const, blocks, timestamp: msg.createdAt };
          }

          // LEGACY FORMAT: Fall back to separate arrays (order may be incorrect)
          // This handles old messages before the events array was added

          // Thinking blocks
          if (Array.isArray(meta.thinking)) {
            meta.thinking.forEach((t) => {
              blocks.push({ type: 'thinking', content: t });
            });
          }

          // Tool calls matched with results
          if (Array.isArray(meta.toolUses)) {
            const results = Array.isArray(meta.toolResults)
              ? (meta.toolResults as Array<{ toolUseId: string; content: unknown }>)
              : [];
            meta.toolUses.forEach((tu) => {
              const matched = results.find((r) => r.toolUseId === tu.toolUseId);
              blocks.push({
                type: 'tool_call',
                toolUseId: tu.toolUseId || '',
                name: tu.name || 'unknown',
                input: tu.input || {},
                result: matched?.content,
                status: 'done',
              });
            });
          }

          // Rich blocks from metadata
          if (Array.isArray(meta.richBlocks)) {
            meta.richBlocks.forEach((rb) => {
              blocks.push({
                type: 'rich_block',
                blockId: rb.blockId,
                blockType: rb.type,
                interactive: rb.interactive,
                component: rb.component,
                payload: rb.payload,
                response: rb.response,
                status: rb.response ? 'answered' : 'expired',
              });
            });
          }

          // Main text content
          if (msg.content) {
            blocks.push({ type: 'text', content: msg.content });
          }

          return { role: 'agent' as const, blocks, timestamp: msg.createdAt };
        });

        setMessages(restored);
      } catch (err) {
        console.error('[useAgentStream] Failed to load history:', err);
        setMessages([]);
      }
    },
    [token],
  );

  /** Clear all messages and queue (used when switching conversations or orgs). */
  const clearMessages = useCallback(() => {
    setMessages([]);
    blocksRef.current = [];
    messageQueueRef.current = [];
    setMessageQueue([]);
  }, []);

  return {
    messages,
    streaming,
    /** True when auto-recovering from a connection interruption */
    isRecovering,
    /** Number of messages waiting in the queue while the agent is busy */
    queueCount: messageQueue.length,
    sendMessage: send,
    stopStreaming,
    respondToRichBlock,
    loadHistory,
    clearMessages,
  };
};
